// =============================================================================
// VINUM - Admin Shell Layout
// Main layout wrapper for Admin Panel with sidebar navigation
// =============================================================================

import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore, ROUTES, selectHasAdminRole } from '@vinum/shared';
import { cn } from '@/lib/cn';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

// =============================================================================
// Icons
// =============================================================================

const Icons = {
  dashboard: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  ),
  users: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  ),
  content: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  ),
  catalog: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
    </svg>
  ),
  orders: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
    </svg>
  ),
  settings: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  analytics: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
  audit: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
    </svg>
  ),
  truck: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
    </svg>
  ),
  menu: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  ),
  collapse: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5" />
    </svg>
  ),
  expand: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5" />
    </svg>
  ),
  logout: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
    </svg>
  ),
  backToApp: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
    </svg>
  ),
  bell: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
    </svg>
  ),
  search: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  ),
};

// =============================================================================
// Navigation Items
// =============================================================================

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  /** Se true, la voce e' visibile solo agli Admin (i Manager non la vedono). */
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', path: ROUTES.ADMIN_DASHBOARD, icon: Icons.dashboard },
  { id: 'users', label: 'Utenti', path: ROUTES.ADMIN_USERS, icon: Icons.users },
  { id: 'wineries', label: 'Cantine', path: ROUTES.ADMIN_WINERIES, icon: Icons.catalog },
  { id: 'content', label: 'Contenuti', path: ROUTES.ADMIN_CONTENT, icon: Icons.content },
  { id: 'catalog', label: 'Catalogo', path: ROUTES.ADMIN_CATALOG, icon: Icons.catalog },
  { id: 'orders', label: 'Ordini', path: ROUTES.ADMIN_ORDERS, icon: Icons.orders },
  { id: 'notifications', label: 'Notifiche', path: ROUTES.ADMIN_NOTIFICATIONS, icon: Icons.bell },
  { id: 'analytics', label: 'Analytics', path: ROUTES.ADMIN_ANALYTICS, icon: Icons.analytics },
  { id: 'stock-outputs', label: 'Import / Export', path: ROUTES.ADMIN_STOCK_OUTPUTS, icon: Icons.truck },
  { id: 'settings', label: 'Impostazioni', path: ROUTES.ADMIN_SETTINGS, icon: Icons.settings },
  { id: 'audit', label: 'Audit Log', path: ROUTES.ADMIN_AUDIT, icon: Icons.audit },
];

const BOTTOM_NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Home', path: ROUTES.ADMIN_DASHBOARD, icon: Icons.dashboard },
  { id: 'users', label: 'Utenti', path: ROUTES.ADMIN_USERS, icon: Icons.users },
  { id: 'wineries', label: 'Cantine', path: ROUTES.ADMIN_WINERIES, icon: Icons.catalog },
  { id: 'notifications', label: 'Notifiche', path: ROUTES.ADMIN_NOTIFICATIONS, icon: Icons.bell },
];

// =============================================================================
// Admin Sidebar
// =============================================================================

interface AdminSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
  isMobileOverlay?: boolean;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ collapsed, onToggle, onNavigate, isMobileOverlay }) => {
  const location = useLocation();
  // Filtra le voci adminOnly per i Manager (non hanno ruolo Admin).
  const hasAdminRole = useAuthStore(selectHasAdminRole);
  const visibleNavItems = NAV_ITEMS.filter((item) => !item.adminOnly || hasAdminRole);

  const isActive = (path: string) => {
    if (path === ROUTES.ADMIN_DASHBOARD) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={cn(
        'bg-wine-900 text-white transition-all duration-300',
        'flex flex-col',
        'w-64',
        // When used as mobile overlay, fill parent container instead of being fixed
        isMobileOverlay
          ? 'h-full'
          : 'fixed left-0 top-0 h-full z-40',
        collapsed && !isMobileOverlay && 'md:w-16'
      )}
    >
      {/* Header */}
      <div className={cn(
        'h-16 flex items-center border-b border-wine-800/50',
        collapsed ? 'md:justify-center md:px-2 justify-between px-4' : 'justify-between px-4'
      )}>
        {/* Logo — always visible on mobile, hidden when collapsed on desktop */}
        <div className={cn(
          'flex items-center gap-2',
          collapsed && 'md:hidden'
        )}>
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-wine-700 flex items-center justify-center">
            <span className="text-white font-bold text-sm">V</span>
          </div>
          <div>
            <h1 className="font-semibold text-sm">VINUM</h1>
            <p className="text-[10px] text-wine-300 uppercase tracking-wider">Admin Panel</p>
          </div>
        </div>
        {/* Collapse button — hidden on mobile */}
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-wine-800 transition-colors hidden md:block"
          aria-label={collapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
        >
          <span className="w-5 h-5 block text-wine-300">
            {collapsed ? Icons.expand : Icons.collapse}
          </span>
        </button>
        {/* Close button — visible only on mobile */}
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-wine-800 transition-colors md:hidden"
          aria-label="Chiudi menu"
        >
          <svg className="w-5 h-5 text-wine-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {visibleNavItems.map((item) => (
            <li key={item.id}>
              <Link
                to={item.path}
                onClick={onNavigate}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
                  'group relative',
                  isActive(item.path)
                    ? 'bg-primary text-white'
                    : 'text-wine-200 hover:bg-wine-800 hover:text-white'
                )}
                title={collapsed ? item.label : undefined}
              >
                <span className="w-5 h-5 flex-shrink-0">{item.icon}</span>
                {/* Labels: always visible on mobile, respect collapsed on desktop */}
                <span className={cn(
                  'font-medium text-sm',
                  collapsed && 'md:hidden'
                )}>{item.label}</span>
                {item.badge && (
                  <span className={cn(
                    'ml-auto bg-gold-500 text-wine-900 text-xs font-medium px-1.5 py-0.5 rounded-full',
                    collapsed && 'md:hidden'
                  )}>
                    {item.badge}
                  </span>
                )}
                {/* Tooltip for collapsed state — desktop only */}
                {collapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 bg-wine-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 hidden md:block">
                    {item.label}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer Actions */}
      <div className="border-t border-wine-800/50 p-2">
        <Link
          to={ROUTES.HOME}
          onClick={onNavigate}
          className={cn(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
            'text-wine-300 hover:bg-wine-800 hover:text-white'
          )}
          title={collapsed ? 'Torna all\'app' : undefined}
        >
          <span className="w-5 h-5">{Icons.backToApp}</span>
          <span className={cn('text-sm', collapsed && 'md:hidden')}>Torna all'app</span>
        </Link>
      </div>
    </aside>
  );
};

// =============================================================================
// Admin Top Bar
// =============================================================================

interface AdminTopBarProps {
  onMobileMenuToggle?: () => void;
}

const AdminTopBar: React.FC<AdminTopBarProps> = ({ onMobileMenuToggle }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate(ROUTES.LOGIN);
    } finally {
      setIsLoggingOut(false);
      setLogoutConfirmOpen(false);
    }
  };

  return (
    <header className="h-16 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between px-4 md:px-6">
      {/* Left: Mobile menu toggle + Back to app */}
      <div className="flex items-center gap-2">
        <button
          onClick={onMobileMenuToggle}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-gray-500 dark:text-slate-400 md:hidden"
          aria-label="Apri menu"
        >
          <span className="w-5 h-5 block">{Icons.menu}</span>
        </button>
        <Link
          to={ROUTES.HOME}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
          title="Torna all'app"
        >
          <span className="w-4 h-4">{Icons.backToApp}</span>
          <span className="hidden sm:inline">Torna all'app</span>
        </Link>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-3">
        {/* Notifications */}
        <Link
          to={ROUTES.ADMIN_NOTIFICATIONS}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-gray-500 dark:text-slate-400 relative"
          title="Notifiche"
        >
          <span className="w-5 h-5 block">{Icons.bell}</span>
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
        </Link>

        {/* Divider */}
        <div className="w-px h-8 bg-gray-200 dark:bg-slate-700" />

        {/* User Menu */}
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {user?.name} {user?.surname}
            </p>
            <p className="text-xs text-gray-500 dark:text-slate-400">Amministratore</p>
          </div>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-wine-700 flex items-center justify-center text-white font-semibold text-sm">
            {user?.name?.charAt(0)?.toUpperCase() || 'A'}
          </div>
          <button
            onClick={() => setLogoutConfirmOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-gray-500 dark:text-slate-400"
            title="Logout"
          >
            <span className="w-5 h-5 block">{Icons.logout}</span>
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        onConfirm={handleLogout}
        title="Confermi il logout?"
        description="Verrai disconnesso e dovrai effettuare di nuovo l'accesso."
        confirmLabel="Esci"
        cancelLabel="Annulla"
        confirmVariant="danger"
        loading={isLoggingOut}
      />
    </header>
  );
};

// =============================================================================
// Admin Shell Component
// =============================================================================

// =============================================================================
// Admin Bottom Navigation (Mobile)
// =============================================================================

const AdminBottomNav: React.FC = () => {
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === ROUTES.ADMIN_DASHBOARD) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 h-16 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 z-40 md:hidden safe-area-inset-bottom">
      <div className="flex h-full">
        {BOTTOM_NAV_ITEMS.map((item) => (
          <Link
            key={item.id}
            to={item.path}
            className={cn(
              'flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors',
              isActive(item.path)
                ? 'text-wine-600 dark:text-wine-400'
                : 'text-gray-400 dark:text-slate-500'
            )}
          >
            <span className="w-5 h-5">{item.icon}</span>
            <span className="text-[10px] font-medium">{item.label}</span>
          </Link>
        ))}
      </div>
    </nav>
  );
};

// =============================================================================
// Admin Shell Component
// =============================================================================

export const AdminShell: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [mobileSidebarAnimating, setMobileSidebarAnimating] = useState(false);
  const location = useLocation();

  // Mobile sidebar animation (two-phase for smooth CSS transitions)
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileSidebarVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setMobileSidebarAnimating(true);
        });
      });
    } else {
      setMobileSidebarAnimating(false);
      const timeout = setTimeout(() => setMobileSidebarVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [mobileMenuOpen]);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Mobile Sidebar Overlay — z-[9999] to stay above bottom nav */}
      {mobileSidebarVisible && (
        <div className="fixed inset-0 z-[9999] md:hidden">
          {/* Backdrop */}
          <div
            className={cn(
              'absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300',
              mobileSidebarAnimating ? 'opacity-100' : 'opacity-0'
            )}
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Sidebar Panel */}
          <div
            className={cn(
              'absolute left-0 top-0 h-full w-64 transition-transform duration-300 ease-out',
              mobileSidebarAnimating ? 'translate-x-0' : '-translate-x-full'
            )}
          >
            <AdminSidebar
              collapsed={false}
              isMobileOverlay
              onToggle={() => setMobileMenuOpen(false)}
              onNavigate={() => setMobileMenuOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <AdminSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </div>

      {/* Main Content Area */}
      <div
        className={cn(
          'transition-all duration-300',
          // No margin on mobile (sidebar is overlay), margin on desktop
          'ml-0 md:ml-16',
          !sidebarCollapsed && 'md:ml-64'
        )}
      >
        {/* Top Bar */}
        <AdminTopBar onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

        {/* Page Content — pb-20 on mobile for bottom nav clearance */}
        <main className="p-4 md:p-6 pb-24 md:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <AdminBottomNav />
    </div>
  );
};

export default AdminShell;
